
import {Component, Vue, Prop, Watch} from "vue-property-decorator";
import {Getter} from "vuex-class";
import {formRules} from "@/utils/formRules";
import {Datetime} from "vue-datetime";
import moment from "moment";
import Timeout = NodeJS.Timeout;
import MylobyAutocomplete from "@/components/generic/autocomplete.vue";
import MylobyLoadMore from "@/components/generic/load-more.vue";

export const DEBOUNCE_TIME_MILLI: number = 500;

@Component({
    components: {
        MylobyAutocomplete,
        MylobyLoadMore,
        Datetime,
    },
})
export default class BookingCreateSelection extends Vue {
    @Prop({default: false})
    public show: boolean;

    @Prop({default: null})
    public bookingStartingDate: any;

    private startDate: string = "";
    private endDate: string = "";
    private rules: object = formRules;
    private valid: boolean = true;
    private wording: any = null;
    private loadingKeys: boolean = false;
    private onlyAvailableKeys: boolean = true;
    private availableKeys: any[] = [];
    private totalKeys: number = 0;
    private keysPagination = {
      page: 0,
      itemsPerPage: 10,
    };
    private timerId: Timeout = null;
    private relatedKey: any = null;
    private bookingCreateSelectionDialog: boolean = false;
    private reservationDestination: string = "myself";
    private keySelected: any = null;
    private userSelected: any = null;
    private search: string = "";
    private notificationType: string = "sms";
    private conflicts: any[] = [];

    @Getter private currentUser: any;
    @Getter private isMobile: boolean;
    @Getter private envCompanyId: number|null;

    get currentDate(): string {
        return new Date().toISOString();
    }

    get formattedKeysValues(): any[] {
        return this.availableKeys.map((key: any) => {
            return { text: key.name, value: key, ...key };
        });
    }

    get isStep1Valid(): boolean {
        return this.keySelected
          && (
            this.reservationDestination === "myself"
            || (this.reservationDestination === "other" && this.userSelected)
          );
    }

    @Watch("show")
    public handlerShow(newVal, oldVal) {
        if (newVal === true) {
            this.bookingCreateSelectionDialog = true;
            this.startDate = this.bookingStartingDate ?
                moment(new Date(
                    this.bookingStartingDate.year,
                    this.bookingStartingDate.month - 1,
                    this.bookingStartingDate.day,
                ) ).format() :
                moment().format();
            this.resetForm();
        }
    }

    @Watch("keySelected", {immediate: false})
    public handlerKeySelected() {
        if (!!this.keySelected) {
            this.getSelectedKey();
            this.checkConflict();
        }
    }

    private resetKeys(): void {
      this.keySelected = null;
      this.conflicts = [];
      if (this.startDate && this.endDate) {
          this.getFirstAvailableKeys();
        }
    }

    private mounted(): void {
        this.wording = {
            ok: this.$i18n.t("actions.next"),
            cancel: this.$i18n.t("actions.cancel"),
        };
    }

    @Watch("search")
    private requestAvailableKeysHandler(): void {
        this.loadingKeys = true;
        // cancel pending call
        clearTimeout(this.timerId);
        // delay new call
        this.timerId = setTimeout(() => { this.getFirstAvailableKeys(); }, DEBOUNCE_TIME_MILLI);
    }

    private async getFirstAvailableKeys(): Promise<void> {
      this.keysPagination.page = 1;
      this.availableKeys = [];
      await this.getAvailableKeys();
    }

    private async getNextAvailableKeys(): Promise<any> {
      this.keysPagination.page++;
      await this.getAvailableKeys();
    }

    private getAvailableKeys(): any {
        this.loadingKeys = true;
        return this.$store.dispatch("bookings/getKeysForBooking", {
            // Filters
            companyId: this.envCompanyId,
            startDate: this.startDate,
            endDate: this.endDate,
            onlyAvailableKeys: this.onlyAvailableKeys,
            // Pagination
            page: this.keysPagination.page,
            itemsPerPage: this.keysPagination.itemsPerPage,
            search: this.search,
        }).then((res) => {
            this.availableKeys = [...this.availableKeys, ...res.rows];
            this.totalKeys = res?.count || 0;
        }).finally(() => {
            this.loadingKeys = false;
        });
    }

    private getSelectedKey() {
        this.$store.dispatch("keys/getById", {
            id: this.keySelected.id,
            options: {
                relays: true,
                company: true,
                holder: true,
            },
        }).then((key) => {
            this.relatedKey = { ...this.keySelected, ...key };
        });
    }

    // Check if the key is not book yet
    private async checkConflict() {
        this.$store.dispatch("bookings/conflict", {
            booking: {
                startDate: moment(this.startDate),
                endDate: moment(this.endDate),
                keyId: this.keySelected.id,
            },
        }).then((conflicts) => {
            this.conflicts = conflicts.bookings.rows;
        });
    }

    get allowMail(): boolean {
        const relatedUser = this.activeUser;
        return !!relatedUser?.email && formRules.isEmail(relatedUser.email);
    }

    get allowPhone(): boolean {
        const relatedUser = this.activeUser;
        return !!relatedUser?.phone;
    }

    get defaultNotificationType(): string {
        if (this.allowMail && !this.allowPhone) {
            return "email";
        }
        if (!this.allowMail && this.allowPhone) {
            return "sms";
        }
        return null;
    }

    get displayNotificationTypeSelection(): boolean {
        return this.keySelected
            && (
                this.reservationDestination === "myself"
                || (this.reservationDestination === "other" && this.userSelected)
            )
            && this.allowMail
            && this.allowPhone;
    }

    get isManager(): boolean {
      return !!this.relatedKey?.legitimateKeyAccess?.keyManagementId;
    }

    get activeUser(): any {
        return this.reservationDestination === "other"
            ? this.userSelected
            : this.currentUser;
    }

    private resetForm() {
        this.reservationDestination = "myself";
        this.relatedKey = null;
        this.keySelected = null;
        this.userSelected = null;
        this.search = "";
        this.endDate = null;
        this.conflicts = [];
    }

    private openBookingModal() {
        this.$emit("nextStep", {
            key: this.relatedKey,
            user: this.activeUser,
            notificationType: this.defaultNotificationType || this.notificationType,
            startDate: this.startDate,
            endDate: this.endDate,
        });
        this.closeDialog();
    }

    private closeDialog(): void {
        this.bookingCreateSelectionDialog = false;
        this.$emit("closeModal");
        this.resetForm();
    }
}
